import { makeAutoObservable } from 'mobx';

import { trimNumber } from '../components/Game/table.render.utils';
import { axiosClient } from '../config/axios-client';
import botErrorStore from './BotErrorStore';
import chatStore from './ChatStore';
import eventLogsStore from './EventLogsStore';
import expertModeStore from './ExpertModeStore';
import gameStore from './GameStore';
import localStorageStore from './LocalStorageStore';
import playerActionStore from './PlayerActionStore';
import { StateStore } from './StateStore';
import stateHolder from './TableHistoryGameStateStore';
import tableStore from './TableStore';
import userSettingsStore from './UserSettingsStore';
import websocketStore from './WebsocketStore';

const DISABLE_LOADING_TIMEOUT = 500;

const BASE_DATE_TIME_URL = '/time-sync';

class CommonStore implements StateStore {
  private isChipsConvertedToBlinds = false;

  public isExpertModeEnabled: boolean;

  public paused = false;

  public isLoading = false;

  public backgroundMode = false;

  public isAllViewingTablesPopulated = false;

  public isSidebarPinned = false;

  public activeTab = '';

  public timeSift = 0;

  constructor() {
    makeAutoObservable(this);
    this.isChipsConvertedToBlinds =
      localStorageStore.isChipsConvertedToBlinds();
    this.isSidebarPinned = localStorageStore.isPinnedSidebar();
  }

  public resetState = (): void => {
    this.paused = false;
    this.isLoading = false;
    this.backgroundMode = false;
    this.isAllViewingTablesPopulated = false;
  };

  public clearAllTableData = (): void => {
    tableStore.resetState();
    gameStore.resetState();
    chatStore.resetState();
    botErrorStore.resetState();
    eventLogsStore.resetState();
    stateHolder.resetState();
    playerActionStore.resetState();
    websocketStore.resetState();
    expertModeStore.resetState();
    this.resetState();
  };

  public getIsChipsConvertedToBlinds = (): boolean =>
    this.isChipsConvertedToBlinds;

  public getIsSidebarPinned = (): boolean => this.isSidebarPinned;

  public convertMoney = (value: number, bb: number): number =>
    trimNumber(this.getIsChipsConvertedToBlinds() ? value / bb : value);

  public convertLimits = (limits: number[]): number[] =>
    limits.map(this.convertLimit);

  public convertLimit = (limit: number): number =>
    userSettingsStore.settings.table.limsX100 ? limit : limit / 100;

  public revertLimits = (limits: number[]): number[] =>
    limits.map(limit =>
      userSettingsStore.settings.table.limsX100 ? limit : limit * 100,
    );

  public revertLimit = (limit: number): number =>
    userSettingsStore.settings.table.limsX100 ? limit : limit * 100;

  public toggleIsChipsConvertedToBlinds = (): void => {
    this.isChipsConvertedToBlinds = !this.isChipsConvertedToBlinds;
    localStorageStore.setChipsConvertedToBlinds(this.isChipsConvertedToBlinds);
  };

  public toggleIsSidebarPinned = (): void => {
    this.isSidebarPinned = !this.isSidebarPinned;
    localStorageStore.setPinnedSidebar(this.isSidebarPinned);
  };

  public unpinSidebar = (): void => {
    this.isSidebarPinned = false;
    localStorageStore.setPinnedSidebar(this.isSidebarPinned);
  };

  public togglePaused = (): void => {
    this.paused = !this.paused;
  };

  public setPaused = (value: boolean): void => {
    this.paused = value;
  };

  public enableLoading = (): void => {
    this.isLoading = true;
  };

  public disableLoading = (): void => {
    setTimeout(
      () => this.setLoading(false),
      tableStore.viewingTableIds.length ? DISABLE_LOADING_TIMEOUT : 0,
    );
  };

  private setLoading = (value: boolean): void => {
    this.isLoading = value;
  };

  public enableBackgroundMode = (): void => {
    this.backgroundMode = true;
  };

  public disableBackgroundMode = (): void => {
    this.backgroundMode = false;
  };

  public setAllViewingTablesPopulated = (value: boolean): void => {
    this.isAllViewingTablesPopulated = value;
  };

  public setActiveTab = (activeTab: string): void => {
    this.activeTab = activeTab;
  };

  public checkActiveTab = (): void => {
    localStorageStore.checkActiveTabs();
    localStorageStore.setActiveTab(this.activeTab);
  };

  public updateTimeShift = (): void => {
    const startTime = Date.now();
    axiosClient.get<string>(BASE_DATE_TIME_URL).then(response => {
      const endTime = Date.now();
      const latency = (endTime - startTime) * 0.8;
      const serverTime = new Date(response.data).getTime();
      this.timeSift = serverTime - (startTime + latency);
    });
  };

  public setIsExpertModeEnabled = (isExpertModeEnabled: boolean): void => {
    this.isExpertModeEnabled = isExpertModeEnabled;
  };
}

const commonStore = new CommonStore();
export default commonStore;
